import { Constants } from "./constants";
import { BROWSE_COUNTRY_KEY, getDynamicSupportedCountries } from "../components/CountryPickerBanner";
import logger from "./logger";


export interface UserLocation {
  latitude: number;
  longitude: number;
  country?: string;
  countryCode?: string;
  city?: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Supported-country helpers (mirrors mobile's _supportedCountryCodes)
// ---------------------------------------------------------------------------

/** Thrown when a search is attempted before we know which market to serve. */
export class MarketUnresolvedError extends Error {
  public readonly code = "MARKET_UNRESOLVED";
  constructor(message = "Market not resolved") {
    super(message);
    this.name = "MarketUnresolvedError";
  }
}

/** Returns true when Spacia has spaces in the given country code. */
export function isCountrySupported(countryCode?: string | null): boolean {
  if (!countryCode) return false;
  const supported = getDynamicSupportedCountries();
  return supported.some(c => c.code.toUpperCase() === countryCode.toUpperCase());
}

/**
 * Resolve the country the user wants to browse.
 *
 * Priority (mirrors mobile's location-detection flow):
 *  1. Explicit user selection stored in localStorage (userBrowseCountry)
 *  2. GPS-cached location that is in a supported country
 *  3. null when neither resolves — the caller must NOT search and must show the
 *     country picker instead.
 *
 * This is intentionally synchronous — the GPS result is cached by
 * requestUserLocation and read back synchronously here.
 */
export function getBrowseCountry(): { countryCode: string; city: string; country: string } | null {
  const supportedList = getDynamicSupportedCountries();
  // 1. Explicit user selection
  try {
    const saved = localStorage.getItem(BROWSE_COUNTRY_KEY);
    if (saved) {
      const match = supportedList.find(c => c.code === saved);
      if (match) {
        return { countryCode: match.code, city: match.defaultCity || "", country: match.name };
      }
    }
  } catch {
    // ignore
  }

  // 2. GPS-cached location (if supported)
  const gps = getCachedLocation();
  if (gps?.countryCode && isCountrySupported(gps.countryCode)) {
    return {
      countryCode: gps.countryCode,
      city:        gps.city || "",
      country:     gps.country || "",
    };
  }

  // 3. Unknown market — no default. Callers block the search and show the
  // country picker rather than browsing another market's spaces.
  return null;
}

/**
 * Guard for any call that would return market-specific inventory.
 *
 * A search must never reach the API unless we know the visitor is browsing an
 * operating country, because an unresolvable market would otherwise silently
 * fall back to a default region's spaces server-side. Throws
 * MarketUnresolvedError so callers can surface the country picker.
 */
export function requireBrowseCountry(): { countryCode: string; city: string; country: string } {
  const resolved = getBrowseCountry();
  if (!resolved) {
    throw new MarketUnresolvedError(
      "Cannot search without a resolved operating country — show the country picker"
    );
  }
  return resolved;
}

/**
 * Resolve the visitor's country from the browser location prompt (with a cached
 * result reused when available) and report whether they need the country picker.
 *
 * GPS is the only signal — no IP geolocation and no timezone guessing, matching
 * mobile. The picker opens when the resolved country is not an operating
 * country, AND when location is unavailable or denied, so a visitor who declines
 * the prompt is never silently served another market's spaces.
 */
export async function detectUnsupportedCountry(): Promise<{
  unsupported: boolean;
  countryCode?: string;
  country?: string;
  /** True when location could not be resolved (denied, unavailable, no fix). */
  locationUnavailable?: boolean;
}> {
  try {
    const cached = getCachedLocation();
    if (cached?.countryCode) {
      // A cached location outside our operating countries still routes to the
      // picker so the visitor can pick a current market.
      if (!isCountrySupported(cached.countryCode)) {
        return {
          unsupported: true,
          countryCode: cached.countryCode,
          country: cached.country,
        };
      }
      return {
        unsupported: false,
        countryCode: cached.countryCode,
        country: cached.country,
      };
    }

    if ("geolocation" in navigator) {
      const gps = await requestUserLocation();
      if (gps?.countryCode) {
        return {
          unsupported: !isCountrySupported(gps.countryCode),
          countryCode: gps.countryCode,
          country: gps.country,
        };
      }
    }

    // Denied, timed out, or the browser has no geolocation support. Show the
    // picker so the visitor can choose a market instead of defaulting to one.
    return { unsupported: true, locationUnavailable: true };
  } catch {
    return { unsupported: true, locationUnavailable: true };
  }
}

/**
 * Reverse geocode coordinates to get address components using Google Maps API
 */
export const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<{ city?: string; countryCode?: string; country?: string }> => {
  try {
    const apiKey = Constants.firebaseConfig.apiKey;
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );
    const data = await response.json();

    if (data.status === "OK" && data.results.length > 0) {
      const result = data.results[0];
      let city = "";
      let countryCode = "";
      let country = "";

      for (const component of result.address_components) {
        if (component.types.includes("locality")) {
          city = component.long_name;
        } else if (!city && component.types.includes("administrative_area_level_1")) {
          city = component.long_name;
        } else if (component.types.includes("country")) {
          countryCode = component.short_name;
          country = component.long_name;
        }
      }

      return { city, countryCode, country };
    }
  } catch (error) {
    logger.error("Error in reverse geocoding:", error);
  }
  return {};
};

/**
 * How long a stored fix stays fresh, matching the `maximumAge` passed to
 * getCurrentPosition so the browser and our own cache agree on freshness.
 *
 * A stored fix is additionally invalidated the moment the browser reports
 * location access as revoked — see getCachedLocation().
 */
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * Request the user's current location from the browser's own location
 * permission prompt. This is the only location signal used for deciding which
 * market to browse — no IP lookup and no timezone inference.
 *
 * Returns null when the visitor declines the prompt, the browser has no
 * geolocation support, or no fix arrives within the timeout. Callers treat
 * null as "ask the user to pick a country" rather than defaulting to a market.
 */
export const requestUserLocation = (): Promise<UserLocation | null> => {
  return new Promise(resolve => {
    if (!("geolocation" in navigator)) {
      logger.log("Geolocation not supported");
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;

        // Perform reverse geocoding to get city and country
        const { city, countryCode, country } = await reverseGeocode(latitude, longitude);

        const location: UserLocation = {
          latitude,
          longitude,
          city,
          countryCode,
          country,
          timestamp: Date.now(),
        };

        // Save to localStorage for header use
        localStorage.setItem("userLocation", JSON.stringify(location));

        resolve(location);
      },
      error => {
        logger.log("Location permission denied or error:", error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        // Must match CACHE_TTL_MS so the browser and our own cache agree on
        // freshness; stale entries are never used.
        maximumAge: CACHE_TTL_MS,
      }
    );
  });
};

/**
 * Get cached location from localStorage
 *
 * The saved fix is only usable while the browser still grants location access.
 * If the visitor revokes permission — in browser settings, OS settings, or via
 * DevTools — the stored coordinates are discarded immediately rather than
 * continuing to be sent as request headers for the rest of the 6h TTL.
 */
export const getCachedLocation = (): UserLocation | null => {
  try {
    const locationStr = localStorage.getItem("userLocation");
    if (!locationStr) return null;

    const location = JSON.parse(locationStr);

    // A stored fix is unusable once location access is revoked.
    if (isGeolocationDenied()) {
      logger.debug("Location permission revoked — discarding cached coordinates");
      localStorage.removeItem("userLocation");
      return null;
    }

    if (location.timestamp && Date.now() - location.timestamp < CACHE_TTL_MS) {
      return location;
    }
  } catch (error) {
    logger.error("Error parsing cached location:", error);
  }
  return null;
};

/**
 * Last known geolocation permission state, kept up to date by
 * watchLocationPermission(). Read synchronously by getCachedLocation().
 */
let lastPermissionState: PermissionState | null = null;

/**
 * Whether the browser last reported location access as denied.
 *
 * Best-effort: defaults to false when the Permissions API is unavailable, so we
 * do not needlessly discard a valid fix on browsers that hide permission state.
 */
const isGeolocationDenied = (): boolean => {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return false;
    return lastPermissionState === "denied";
  } catch {
    return false;
  }
};

/**
 * Track the browser's geolocation permission state and drop the cached fix the
 * moment access is revoked, so a visitor who switches location off stops being
 * located without waiting for the TTL to expire.
 *
 * Returns a teardown function.
 */
export const watchLocationPermission = (onChange?: (state: PermissionState) => void): (() => void) => {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return () => {};

    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then(status => {
        if (cancelled) return;
        lastPermissionState = status.state;
        status.onchange = () => {
          lastPermissionState = status.state;
          if (status.state === "denied") {
            clearLocation();
          }
          onChange?.(status.state);
        };
        onChange?.(status.state);
      })
      .catch(() => {
        // Permissions API unavailable for geolocation — nothing to watch.
      });

    return () => {
      cancelled = true;
    };
  } catch {
    return () => {};
  }
};

/**
 * Save location with country/city info
 */
export const saveLocation = (location: UserLocation): void => {
  localStorage.setItem(
    "userLocation",
    JSON.stringify({
      ...location,
      timestamp: Date.now(),
    })
  );
};

/**
 * Clear cached location
 */
export const clearLocation = (): void => {
  localStorage.removeItem("userLocation");
};
